import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Request } from "./requests";
import {
  TCreateAppointment,
  TCreateDoctor,
  TRescheduleAppointment,
  UploadDoctorMediaOptions,
  UseGetAllDoctorsProps,
} from "./types";
import customToast, { ToastType } from "../components/Toast";
import { useAppDispatch } from "../hooks/useAppDispatch";
import { setDoctorInfo } from "../store/authSlice";
import { useNavigate } from "react-router-dom";
import { normalizeDateTime } from "../features/patients/utils";

export const useCreateDoctor = () => {
  return useMutation({
    mutationFn: (data: TCreateDoctor) => {
      return Request.post("/doctor/sign-up", data)
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to create doctor", ToastType.error);
    },
  });
};

export const useGetAllDoctors = ({
  limit,
  page,
  specialty,
  search,
}: UseGetAllDoctorsProps) => {
  const { data, isLoading, isRefetching, error, isSuccess } = useQuery({
    queryKey: ["getAllDoctors", limit, page],
    queryFn: () =>
      Request.get(`/doctor/find-all?limit=${limit}&page=${page}`)
        .then((res) => res.data)
        .catch((err) => {
          throw err?.response?.data;
        }),
    refetchOnWindowFocus: false,
  });

  const serverData = data?.data;
  let filteredDoctors = serverData?.doctors || [];

  // Frontend specialty filtering
  if (specialty && specialty.toLowerCase() !== "all") {
    filteredDoctors = filteredDoctors.filter((doctor: any) => {
      const doctorSpecialties = Array.isArray(doctor.specialty)
        ? doctor.specialty.map((s: any) => String(s).trim().toLowerCase())
        : [
            String(doctor.specialty || "")
              .trim()
              .toLowerCase(),
          ];

      return doctorSpecialties.some((spec: string) =>
        spec.includes(specialty.trim().toLowerCase()),
      );
    });
  }

  // Frontend search filtering
  if (search && search.trim()) {
    const searchLower = search.toLowerCase();
    filteredDoctors = filteredDoctors.filter((doctor: any) =>
      doctor.name?.toLowerCase().includes(searchLower),
    );
  }

  return {
    doctors: filteredDoctors,
    total: serverData?.total,
    page: serverData?.page,
    lastPage: serverData?.lastPage,
    isLoading,
    isSuccess,
    error,
    isRefetching,
  };
};

export const useGetDoctorNameById = (doctorId: string) => {
  return useQuery({
    queryKey: ["doctor-name", doctorId],
    queryFn: async () => {
      const response = await Request.get(`/doctors/${doctorId}/name`);
      return response.data?.data?.name || ""; // Navigating to data.name as per backend response
    },
    enabled: !!doctorId,
  });
};

export const useGetDoctorNamesByIds = (doctorIds: string[]) => {
  return useQuery({
    queryKey: ["doctor-names", doctorIds],
    queryFn: async () => {
      const idsParam = doctorIds.join(",");
      const response = await Request.get(`/doctor/by-ids?ids=${idsParam}`);
      return response.data?.data || [];
    },
    enabled: doctorIds.length > 0,
  });
};

export const useVerifyOtp = () => {
  return useMutation({
    mutationFn: (data: { doctorId: string; otp: string }) => {
      return Request.post("/doctor/authenticate-otp", data)
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to verify email", ToastType.error);
    },
  });
};

export const useResendOtp = () => {
  return useMutation({
    mutationFn: (data: { doctorId: string }) => {
      return Request.post("/doctor/resend-otp", data)
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onSuccess: () => {
      customToast(
        "A new verification code has been sent to your email",
        ToastType.success,
      );
    },
    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to resend verification code", ToastType.error);
    },
  });
};

export const useVerifyLicense = () => {
  return useMutation({
    mutationFn: (data: {
      doctorId: string;
      licenseNumber: string;
      yoe: number;
      specialty: string[];
    }) => {
      return Request.post("/doctor/submit-medical-license", data)
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to submit medical license", ToastType.error);
    },
  });
};

export const useLogin = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (data: { email: string; password: string }) => {
      return Request.post("/auth/login", data)
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onSuccess: (data) => {
      dispatch(setDoctorInfo(data?.data?.doctor));
      sessionStorage.clear();
      customToast("Login successfully", ToastType.success);
      localStorage.setItem("access_token", data?.data?.access_token);
      Request.defaults.headers.common["Authorization"] =
        `Bearer ${data.data.access_token}`;
      if (data?.data?.doctor?.verified) {
        navigate("/doctor");
      } else {
        navigate("/signup?tab=verify-email");
      }
    },
    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to login", ToastType.error);
    },
  });
};

export const useGetPatients = ({
  limit,
  page,
  search,
  riskCategory,
}: //doctorId,
{
  limit: number;
  page: number;
  search?: string;
  riskCategory?: string;
  //doctorId: string;
}) => {
  const { data, isLoading, isRefetching, error, isSuccess } = useQuery({
    queryKey: ["getPatients", limit, page, search, riskCategory],
    queryFn: () =>
      Request.get(
        `/patient/find-all?limit=${limit}&page=${page}${
          search ? `&search=${encodeURIComponent(search)}` : ""
        }${
          riskCategory && riskCategory.toLowerCase() !== "all"
            ? `&riskCategory=${encodeURIComponent(riskCategory)}`
            : ""
        }`,
      )
        .then((res) => res.data)
        .catch((err) => {
          throw err?.response?.data;
        }),
    refetchOnWindowFocus: false,
  });

  return { data, isLoading, isSuccess, error, isRefetching };
};

export const useRequestResetPassword = () => {
  return useMutation({
    mutationFn: (data: { email: string }) => {
      return Request.post("/auth/request-reset-password", data)
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to request for password reset", ToastType.error);
    },
  });
};

export const useGetAppointments = ({
  doctorId,
  limit,
  page,
  category,
  mode,
  appointmentStatus,
  search,
  filterType, // ✅ Add this
}: {
  doctorId: string;
  limit?: number;
  page?: number;
  category?: string;
  mode?: string;
  appointmentStatus?: string;
  search?: string;
  filterType?: "upcoming" | "past"; // ✅ Add this
}) => {
  const { data, isLoading, isRefetching, error, isSuccess } = useQuery({
    queryKey: [
      "getAppointments",
      doctorId,
      limit,
      page,
      category || "",
      mode || "",
      appointmentStatus || "",
      search || "",
      filterType || "", // ✅ Add to query key
    ],

    queryFn: () =>
      Request.get("/appointment/find-all", {
        params: {
          doctorId,
          limit,
          page,
          category: category || undefined,
          mode: mode || undefined,
          appointmentStatus,
          search,
          filterType, // ✅ Send this in params
        },
      })
        .then((res) => res.data)
        .catch((err) => {
          throw err?.response?.data;
        }),
  });

  return { data, isLoading, isSuccess, error, isRefetching };
};

export const useResetPassword = () => {
  return useMutation({
    mutationFn: (data: { resetToken: string; password: string }) => {
      return Request.post("/auth/reset-password", {
        token: data?.resetToken,
        password: data?.password,
      })
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to reset password", ToastType.error);
    },
  });
};

export const useGetDashboardOverview = ({ doctorId }: { doctorId: string }) => {
  const { data, isLoading, error } = useQuery({
    queryKey: ["getDashboardOverview", doctorId],
    queryFn: () =>
      Request.get(`/doctor/dashboard-overview?doctorIdString=${doctorId}`)
        .then((res) => {
          return res?.data?.data;
        })
        .catch((err) => {
          console.error("API Error:", err?.response?.data || err);
          throw err;
        }),
  });
  return { data, isLoading, error };
};

export const useGetAppointmentOverview = ({
  doctorId,
  year,
  period,
}: {
  doctorId: string;
  year?: string;
  period?: "week" | "month" | "year";
}) => {
  const { data, isLoading } = useQuery({
    queryKey: ["getAppointmentOverview", doctorId, period],
    queryFn: () =>
      Request.get(
        `/appointment/overview?doctorIdString=${doctorId}&period=${period}`,
      )
        .then((res) => {
          return res.data;
        })
        .catch((err) => {
          throw err?.response?.data;
        }),
  });

  return { data, isLoading };
};

export const useCreateAppointment = ({ handleSuccess }) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: TCreateAppointment) => {
      const copy = { ...data };
      const { appointmentStatus, notes, address, ...filteredData } = copy;

      return Request.post("/appointment/create-appointment", {
        ...filteredData,
        date: normalizeDateTime(filteredData?.date),
        startTime: normalizeDateTime(filteredData?.startTime),
        endTime: normalizeDateTime(filteredData?.endTime),
        appointmentStatus: data?.appointmentStatus,
        notes: data?.notes,
        address: data?.address,
      })
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onSuccess: (_, { patientId }) => {
      customToast("Appointment scheduled successfully", ToastType.success);
      queryClient.invalidateQueries({ queryKey: ["getAppointments"] });
      queryClient.invalidateQueries({ queryKey: ["getAppointmentOverview"] });
      queryClient.invalidateQueries({
        queryKey: ["getPatientDetails", patientId],
      });
      handleSuccess();
    },
    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to create appointment", ToastType.error);
    },
  });
};

export const useRescheduleAppointment = ({ handleSuccess }) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TRescheduleAppointment) => {
      const { appointmentStatus, ...rest } = data;

      return Request.patch("/appointment/reschedule", {
        ...rest,
        date: normalizeDateTime(rest?.date),
        startTime: normalizeDateTime(rest?.startTime),
        endTime: normalizeDateTime(rest?.endTime),
        appointmentStatus: data?.appointmentStatus,
      })
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onSuccess: () => {
      customToast("Appointment rescheduled successfully", ToastType.success);
      queryClient.invalidateQueries({ queryKey: ["getAppointments"] });
      handleSuccess();
    },
  });
};
export const useUpdateDoctor = () => {
  const queryClient = useQueryClient();
  const dispatch = useAppDispatch();
  return useMutation({
    mutationFn: ({ data, doctorId }: { data: any; doctorId: string }) => {
      const isFormData = data instanceof FormData;
      return Request.patch(`/doctor/update/${doctorId}`, data, {
        headers: isFormData ? { "Content-Type": "multipart/form-data" } : {},
      })
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onSuccess: (data) => {
      dispatch(setDoctorInfo(data?.data));
      customToast("Updated profile successfully", ToastType.success);
    },
    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to update profile", ToastType.error);
    },
  });
};

export const useCancelAppointment = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ appointmentId }: { appointmentId: string }) => {
      return Request.delete(
        `/appointment/cancel?appointmentId=${appointmentId}`,
      )
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },
    onSuccess: (data) => {
      customToast("Cancelled appointment successfully", ToastType.success);
      queryClient.invalidateQueries({ queryKey: ["getAppointments"] });
    },
    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to cancel appointment", ToastType.error);
    },
  });
};

export const useCompleteAppointment = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ appointmentId }: { appointmentId: string }) => {
      return Request.patch(
        `/appointment/complete?appointmentId=${appointmentId}`,
      )
        .then((res) => res?.data)
        .catch((err) => {
          throw err?.response?.data?.message;
        });
    },

    onSuccess: () => {
      customToast("Appointment marked as completed", ToastType.success);
      queryClient.invalidateQueries({ queryKey: ["getAppointments"] });
    },

    onError: (err) => {
      typeof err === "string"
        ? customToast(err, ToastType.error)
        : customToast("Unable to complete appointment", ToastType.error);
    },
  });
};

export const useUploadDoctorMedia = () => {
  return useMutation({
    mutationFn: async ({
      doctorId,
      tag,
      formData,
      onProgress,
    }: UploadDoctorMediaOptions) => {
      console.log("🚀 Uploading media for doctorId:", doctorId);
      const res = await Request.post(
        `/doctor/${doctorId}/media?tag=${tag}`,
        formData,
        {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 120000,
          onUploadProgress: (progressEvent) => {
            if (progressEvent.total && onProgress) {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total,
              );
              onProgress(percentCompleted);
            }
          },
        },
      );

      // ✅ FIX: return only the actual URL

      const imageUrl = res?.data?.data?.url || res?.data?.url;
      if (!imageUrl) {
        console.warn("Upload response:", res?.data);
        throw new Error("Image URL was not returned from upload");
      }

      if (res.status >= 500) {
        console.error("Server error during upload:", res);
        throw new Error("Server failed to upload doctor media");
      }

      return imageUrl;
    },

    onError: (error: any) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to upload doctor media";
      customToast(errorMessage, ToastType.error);
      throw error;
    },
  });
};

export const useAttachMediaToDoctor = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      doctorId,
      url,
      tag,
    }: {
      doctorId: string;
      url: string;
      tag: "profile-picture" | "license";
    }) => {
      if (!doctorId || !url) {
        throw new Error("Invalid doctorId or media URL");
      }

      console.log("📎 Attaching media", { doctorId, url, tag });

      const res = await Request.post(`/doctor/${doctorId}/media?tag=${tag}`, {
        url,
      });

      return res.data;
    },

    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: ["doctorMedia", variables.doctorId],
      });
      customToast("Media attached to doctor successfully", ToastType.success);
    },

    onError: (error: any) => {
      console.error("Attach failed", error);
    },

    // ✅ Prevent auto-retry on failure
    retry: false,
  });
};

export const useCreateNotification = () => {
  return useMutation({
    mutationFn: async (data: {
      userId: string;
      title: string;
      message: string;
      type?: string;
    }) => {
      const response = await Request.post("/notifications", data);
      return response.data;
    },
  });
};

export const useGetNotificationsByUserId = (userId: string) => {
  return useQuery({
    queryKey: ["notifications", userId],
    queryFn: async () => {
      const response = await Request.get(`/notifications/${userId}`);
      return response.data.data; // ✅ return just the array
    },
    enabled: !!userId,
  });
};

export const useMarkNotificationAsRead = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      const response = await Request.patch(
        `/notifications/read/${notificationId}`,
      );
      return response.data;
    },
    onSuccess: () => {
      // ✅ Refetch notifications after marking one as read
      queryClient.invalidateQueries({ queryKey: ["notifications", userId] });
    },
  });
};

export const useMarkAllNotificationsAsRead = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const response = await Request.patch(`/notifications/read-all/${userId}`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications", userId] });
    },
  });
};

export const useUnreadNotificationCount = (userId: string) => {
  return useQuery({
    queryKey: ["unreadNotificationCount", userId],
    queryFn: async () => {
      const response = await Request.get(
        `/notifications/unread-count/${userId}`,
      );
      return response.data.data; // ✅ Extract the number directly
    },
    enabled: !!userId,
  });
};

export const useDeleteNotification = (userId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (notificationId: string) => {
      await Request.delete(`/notifications/${notificationId}`);
    },
    onSuccess: () => {
      // Invalidate and refetch notifications list

      queryClient.invalidateQueries({ queryKey: ["notifications", userId] });
    },
  });
};
